// app/src/lib/cashflow/capex.js — capital-expenditure rollup.
//
// Capex = a one-off capital purchase (equipment, build-out, furniture) the
// operator has tagged 'capex' via the Spending tab's category picker. Unlike the
// operating categories, capex is deliberately EXCLUDED from the last-30-day
// operating trend and the cost reports — it's lumpy and one-off, so it distorts
// run-rate comparisons. This report gives it its own home: a YTD-vs-last-year
// view of what capital went where, and how it was funded (card vs. bank/check).
//
// A charge is capex when its effective spend-category is 'capex' — set as a
// per-charge override (`tx:<id>`) or a per-merchant rule (normalized merchant),
// the SAME db.spendRule store the Spending tab writes. Because merchant rules
// key on the merchant name (not the account), tagging "ABC Restaurant Supply" as
// capex on a card charge ALSO captures a later CHECK to the same vendor — so
// capex spans card and checking without a separate mechanism.
//
// Limitation (v1): a one-off capex paid by check from a vendor never seen on the
// card has no rule to match. Tag that vendor once (on any card charge, or it
// falls out) — a future iteration may let checking charges be categorized
// directly.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { getRules, ruleKey } from './spendCategory.js';
import { creditAccountIds } from './accounts.js';

// Payments / transfers are never spend (a card bill payment is the settlement of
// charges already counted); they can't be capex.
const NON_SPEND_CATS = new Set(['LOAN_PAYMENTS', 'TRANSFER_IN', 'TRANSFER_OUT']);

/** True when a transaction (card OR checking) is tagged capex via a spend rule. */
export function isCapex(tx, rules) {
  if (!tx || tx.pending) return false;
  if (NON_SPEND_CATS.has(tx.category)) return false;
  const cat = rules.get('tx:' + tx.plaidTxId) || rules.get(ruleKey(tx));
  return cat === 'capex';
}

/**
 * Roll up capex across all accounts. Refunds (negative) net against charges.
 * @returns {Promise<null | {
 *   items: Array<{plaidTxId,date,amountCents,merchant,method:'card'|'bank'}>,  // newest first
 *   byMethod: { card:number, bank:number },
 *   byMerchant: Array<{merchant:number, cents:number}>,  // this-year, desc
 *   ytdCents:number, lastYearCents:number, totalCents:number,
 *   thisYear:number, priorYear:number,
 * }>}
 */
export async function summarize({ now = new Date() } = {}) {
  const c = await currentClaims();
  if (!c) return null;
  const userId = c.sub;
  const [rules, creditSet, txsAll] = await Promise.all([
    getRules(userId),
    creditAccountIds(userId),
    db.plaidTransactions.where('userId').equals(userId).toArray(),
  ]);

  const items = [];
  for (const tx of txsAll) {
    if (!isCapex(tx, rules)) continue;
    if (!tx.date) continue;
    items.push({
      plaidTxId: tx.plaidTxId,
      date: tx.date,
      amountCents: tx.amountCents || 0,
      merchant: tx.merchantName || tx.description || 'Capital expense',
      method: creditSet.has(tx.plaidAccountId) ? 'card' : 'bank',
    });
  }
  items.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)); // newest first

  const thisYear = now.getFullYear();
  const priorYear = thisYear - 1;
  const inYear = (y) => items.filter((i) => i.date.startsWith(`${y}-`));
  const sum = (arr) => arr.reduce((s, i) => s + i.amountCents, 0);

  const ytd = inYear(thisYear);
  const ytdCents = sum(ytd);
  const lastYearCents = sum(inYear(priorYear));

  const byMethod = { card: 0, bank: 0 };
  for (const i of ytd) byMethod[i.method] += i.amountCents;

  const merchantMap = new Map();
  for (const i of ytd) merchantMap.set(i.merchant, (merchantMap.get(i.merchant) || 0) + i.amountCents);
  const byMerchant = [...merchantMap.entries()]
    .map(([merchant, cents]) => ({ merchant, cents }))
    .filter((m) => m.cents > 0)
    .sort((a, b) => b.cents - a.cents);

  return {
    items, byMethod, byMerchant,
    ytdCents, lastYearCents, totalCents: sum(items),
    thisYear, priorYear,
  };
}
